import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import trialService from '../../services/api/trialService';

interface SubscriptionRouteProps {
  children: React.ReactNode;
  /** 断ったときの戻り先。新UI（/test-ui）の中では新UIのホームを渡す */
  redirectTo?: string;
}

/**
 * サブスクリプション（有料契約）を持つ組織のみアクセスを許可するルートガード。
 *
 * - mount 時に trialService.getTrialStatus() を1回呼び、has_subscription を判定する
 * - 取得中は FeatureRoute と同じ "Loading..." 表示
 * - has_subscription === true なら children を表示
 * - card_lock_exempt === true（OnboardingV2 リリース前から存在する既存組織）は
 *   サブスク無しでも children を表示する
 * - どちらでもなければ redirectTo（既定は /home）へリダイレクト
 * - API エラー時は fail-open（children を表示し、既存ページを止めない）
 *
 * Redux は使わずローカル state + trialService の直接呼び出しで完結させる
 * （trial-status の Redux/Provider 化は非スコープ）。
 */
const SubscriptionRoute: React.FC<SubscriptionRouteProps> = ({ children, redirectTo = '/home' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(true);

  useEffect(() => {
    let cancelled = false;

    trialService
      .getTrialStatus()
      .then((status) => {
        if (cancelled) return;
        setHasSubscription(status.has_subscription === true || status.card_lock_exempt === true);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // fail-open: エラー時は既存ユーザーを止めないよう children を表示する
        setHasSubscription(true);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!hasSubscription) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export { SubscriptionRoute };
export default SubscriptionRoute;
